import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface Server {
  id: string;
  serverName: string;
  serverType: 'stdio' | 'http' | 'sse' | 'websocket' | 'grpc';
  status: 'connected' | 'disconnected' | 'error';
  lastSync?: string;
  lastError?: string;
  capabilities?: any;
  clientId: string;
}

export const useServers = (clientId?: string) => {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchServers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.servers.list(clientId);
      setServers(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch servers');
    } finally {
      setLoading(false);
    }
  };

  const connectServer = async (
    serverName: string,
    url: string,
    clientId?: string
  ) => {
    try {
      const newServer = await api.servers.connect(serverName, url, clientId);
      await fetchServers(); // Refresh list
      return newServer;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to connect server');
    }
  };

  const disconnectServer = async (id: string) => {
    try {
      await api.servers.delete(id);
      setServers(servers.filter(s => s.id !== id));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to disconnect server');
    }
  };

  useEffect(() => {
    fetchServers();
  }, [clientId]);

  return {
    servers,
    loading,
    error,
    fetchServers,
    connectServer,
    disconnectServer
  };
};
